import type { MembershipRole } from '@/store/types'
import type { ApiResult } from './types'
import { get } from '@/backend/api'
import { isErrorResponse } from '@/backend/error/types'
import { errorMessages, parseError } from '@/backend/error'

/**
 * An API key the member owns that a membership change would affect.
 *
 * `lost_actions` are the key's actions the member's new role no longer grants
 * (for a removal, every action the key holds, since the key is deleted).
 */
export type AffectedApiKey = {
  id: number
  name: string
  lost_actions: string[]
}

/**
 * Previews which of a member's API keys a membership change would affect.
 *
 * Pass `newRole` to preview a role downgrade (keys that lose at least one
 * action). Omit it to preview removing the member (all their keys for the
 * team are deleted).
 */
export const getMembershipApiKeyImpact = async (
  membershipId: number,
  newRole?: MembershipRole,
): Promise<ApiResult<AffectedApiKey[]>> => {
  try {
    const response = await get<{ affected_api_keys: AffectedApiKey[] }>(
      `memberships/${membershipId}/usage`,
      newRole ? { role: newRole } : undefined,
    )
    return { ok: true, data: response.data.affected_api_keys }
  } catch (error) {
    if (!isErrorResponse(error)) {
      throw error
    }
    return { ...parseError(error, errorMessages.API_KEYS_LOAD), ok: false }
  }
}
